import React, { useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding,
    faTachometerAlt,
    faTractor,
    faWater,
    faUsers,
    faCogs,
    faTable,
    faTemperature0,
    faRandom,
    faFish,
    faNetworkWired,
    faMemory,
    faSnowplow,
    faRetweet,
    faHouseFloodWater,
    faBell,
    faStopwatch,
    faChartColumn,
    faUserPen,
    faBuildingNgo, 
    faClock,
    faChartLine,
    faHistory,
    faChevronDown,
    faChevronRight,
    faChartArea
} from '@fortawesome/free-solid-svg-icons'; // Import the faUsers icon
import { NavLink } from 'react-router-dom';
import {UserContext} from "./services/UserContext";
import {useTranslation} from "react-i18next";


const Sidebar = ({ isSidebarOpen }) => {
    const { userRoles } = useContext(UserContext);
    const { t } = useTranslation();
    const [isPondSensorDataOpen, setIsPondSensorDataOpen] = useState(false);
    const [isPeripheralManagementOpen, setIsPeripheralManagementOpen] = useState(false);
    const [isSuperAdminReportsOpen, setIsSuperAdminReportsOpen] = useState(false);

    return (
        <div
            className={`bg-sky-800 text-white h-screen flex flex-col fixed top-0 left-0 transform transition-transform duration-300 ease-in-out item-center ${
                isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            style={{width: '16rem'}} // Equivalent to w-64 in Tailwind
        >
            <h1 className="text-lg font-bold p-4 flex items-center"><img src="/favicon.ico" alt="Logo" className="h-12 w-12 mx-4 my-2 rounded"/> {t('app_name')}</h1>

            <ul className="flex-1 overflow-y-auto">
            <li>
                <NavLink
                        to="/"
                        className={({isActive}) =>
                            `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                        }
                    >
                        <FontAwesomeIcon icon={faTachometerAlt} className="mr-2"/>
                    {t('dashboard')}
                    </NavLink>
                </li>

                {userRoles.includes('super_admin') && (
                    <>
                        <li>
                            <div
                                className="p-4 shadow-blue-950 shadow flex items-center justify-between hover:bg-sky-700 cursor-pointer"
                                onClick={() => setIsSuperAdminReportsOpen(!isSuperAdminReportsOpen)}
                            >
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={faChartArea} className="mr-2"/>
                                    {t('data_analysis_reports') || 'Data Analysis & Reports'}
                                </div>
                                <FontAwesomeIcon
                                    icon={isSuperAdminReportsOpen ? faChevronDown : faChevronRight}
                                    className="text-sm"
                                />
                            </div>
                            {isSuperAdminReportsOpen && (
                                <ul className="bg-sky-900">
                                    <li>
                                        <NavLink
                                            to="/farm-overview"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTable} className="mr-2 text-sm"/>
                                            {t('farm_overview') || 'Farm Overview'}
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/super-admin-water-quality-table"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTable} className="mr-2 text-sm"/>
                                            {t('water_quality') || 'Water Quality Table'}
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/super-admin-peripheral-status-report"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faSnowplow} className="mr-2 text-sm"/>
                                            {t('peripheral_status_report') || 'Peripheral Status Report'}
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/sensor-data-averages-super-admin"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-sm"/>
                                            {t('sensor_data_graph') || 'Sensor Data Averages'}
                                        </NavLink>
                                    </li>
                                </ul>
                            )}
                        </li>
                        <li>
                            <NavLink
                                to="/companies"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faBuilding} className="mr-2"/>
                                {t('company')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/ngos"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faBuildingNgo} className="mr-2"/>
                                {t('ngo')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/farms"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faHouseFloodWater} className="mr-2"/>
                                {t('farm')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/all-farmer-users"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faUsers} className="mr-2"/>
                                {t('all_farmer_users')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/ponds"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faWater} className="mr-2"/>
                                {t('pond')}
                            </NavLink>
                        </li>
                        {/* Data Analysis & Reports Submenu */}

                        <li>
                            <NavLink
                                to="/fish-manage"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faFish}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('fish_management')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/sensor-manage"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faCogs}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('sensor_management')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/device-manage"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faMemory}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('device_management')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/conductor-manage"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faNetworkWired}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('conductor_management')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/device-interval"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faStopwatch}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('device_interval')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/peripheral-manage"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faSnowplow}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('peripheral_management')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/peripheral-threshold-manage"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faRetweet}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('peripheral_threshold_management')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/data-tempering"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faUserPen}
                                                 className="mr-2"/> {/* Assuming faCogs for sensor management icon */}
                                {t('data_modification')}
                            </NavLink>
                        </li>
                    </>
                )}

                {userRoles.includes('admin') && (
                    <>
                        <li>
                            <NavLink
                                to="/farm"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faTractor} className="mr-2"/>
                                {t('farm')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/users"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faUsers} className="mr-2"/>
                                {t('user')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/farm-pond-status"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faWater} className="mr-2"/>
                                {t('farm_pond')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/water-quality-graph"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faChartColumn} className="mr-2"/>
                                {t('water_quality')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/water-quality-table"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faTable} className="mr-2"/>
                                {t('water_quality_table_admin')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/admin-peripheral-status-report"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faSnowplow} className="mr-2"/>
                                {t('peripheral_status_report') || 'Peripheral Status Report'}
                            </NavLink>
                        </li>
                    </>

                )}

                {userRoles.includes('ngo_admin') && (
                    <>
                        <li>
                            <NavLink
                                to="/ngo-farm-pond-status"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faWater} className="mr-2"/>
                                {t('farm_pond')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/water-quality-graph-ngo"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faChartColumn} className="mr-2"/>
                                {t('water_quality')}
                            </NavLink>
                        </li>

                    </>

                )}

                {userRoles.includes('farmer') && (
                    <>
                        <li>
                            <NavLink
                                to="/pond"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faWater} className="mr-2"/>
                                {t('pond')}
                            </NavLink>
                        </li>
                        {/* Pond Sensor Data Submenu */}
                        <li>
                            <div 
                                className="p-4 shadow-blue-950 shadow flex items-center justify-between hover:bg-sky-700 cursor-pointer"
                                onClick={() => setIsPondSensorDataOpen(!isPondSensorDataOpen)}
                            >
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={faChartArea} className="mr-2"/>
                                    {t('pond_sensor_data') || 'Pond Sensor Data'}
                                </div>
                                <FontAwesomeIcon 
                                    icon={isPondSensorDataOpen ? faChevronDown : faChevronRight} 
                                    className="text-sm"
                                />
                            </div>
                            {isPondSensorDataOpen && (
                                <ul className="bg-sky-900">
                                    <li>
                                        <NavLink
                                            to="/pond-sensor-data"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-sm"/>
                                            {t('pond_sensor_data_graphs') || 'Graphs'}
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/pond-sensor-data-table"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTable} className="mr-2 text-sm"/>
                                            {t('pond_sensor_data_table') || 'Table'}
                                        </NavLink>
                                    </li>
                                </ul>
                            )}
                        </li>
                        <li>
                            <NavLink
                                to="/pond-status"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faTemperature0} className="mr-2"/>
                                {t('pond_status')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/sensor-thresholds"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faRandom} className="mr-2"/>
                                {t('sensor_thresholds')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                to="/sensor-alert"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faBell} className="mr-2"/>
                                {t('sensor_alert')}
                            </NavLink>
                        </li>
                        {/*<li>*/}
                        {/*    <NavLink*/}
                        {/*        to="/culture-cycle"*/}
                        {/*        className={({isActive}) =>*/}
                        {/*            `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`*/}
                        {/*        }*/}
                        {/*    >*/}
                        {/*        <FontAwesomeIcon icon={faClockRotateLeft} className="mr-2"/>*/}
                        {/*        {t('culture_cycle')}*/}
                        {/*    </NavLink>*/}
                        {/*</li>*/}
                        <li>
                            <NavLink
                                to="/schedule"
                                className={({isActive}) =>
                                    `p-4 shadow-blue-950 shadow flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700' : ''}`
                                }
                            >
                                <FontAwesomeIcon icon={faClock} className="mr-2"/>
                                {t('schedule')}
                            </NavLink>
                        </li>
                        {/* Peripheral Management Submenu */}
                        <li>
                            <div 
                                className="p-4 shadow-blue-950 shadow flex items-center justify-between hover:bg-sky-700 cursor-pointer"
                                onClick={() => setIsPeripheralManagementOpen(!isPeripheralManagementOpen)}
                            >
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={faCogs} className="mr-2"/>
                                    {t('peripheral_management') || 'Peripheral Management'}
                                </div>
                                <FontAwesomeIcon 
                                    icon={isPeripheralManagementOpen ? faChevronDown : faChevronRight} 
                                    className="text-sm"
                                />
                            </div>
                            {isPeripheralManagementOpen && (
                                <ul className="bg-sky-900">
                                    <li>
                                        <NavLink
                                            to="/peripheral-status-report"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faSnowplow} className="mr-2 text-sm"/>
                                            {t('peripheral_status_report') || 'Status Report'}
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink
                                            to="/peripheral-control-history"
                                            className={({isActive}) =>
                                                `pl-8 pr-4 py-3 flex items-center hover:bg-sky-700 ${isActive ? 'bg-sky-700 border-r-4 border-white' : ''}`
                                            }
                                        >
                                            <FontAwesomeIcon icon={faHistory} className="mr-2 text-sm"/>
                                            {t('peripheral_control_history') || 'Control History'}
                                        </NavLink>
                                    </li>
                                </ul>
                            )}
                        </li>
                    </>

                )}
            </ul>
        </div>
    );
}

export default Sidebar;
